import React, { useCallback, useRef } from 'react'

import { createPopper, Instance, Placement } from '@popperjs/core'

import * as S from './styles'

export type TooltipProps = {
  action?: string
  interactive?: boolean
  position?: Placement
  title?: string
  children: React.ReactNode
  delay?: number
} & ({ content: React.ReactNode; description?: never } | { content?: never; description: string })

/**
 * - [`Inter UI Documentation`](https://inter-ui.bancointer.com.br/?path=/story/data-display-tooltip-since-v1-0-0--simple)
 * - [`Figma Orange DS Documentation`](https://www.figma.com/file/1JFZTVxalrwvmUHCWBf2Pt/DS-%2F-Components-%2F-Mobile-%26-App?node-id=5914-6890&t=wAMKvrJkIX4MccKE-0)
 *
 * **Code example**
 * ```tsx
 * import React from 'react'
 *
 * import { Tooltip } from '@interco/inter-ui/components/Tooltip'
 * import { Button } from '@interco/inter-ui/components/Button'
 *
 * export const MyPage = () => {
 *   return (
 *     <Tooltip description="Texto que será exibido no tooltip">
 *       <Button>
 *         Aqui pode ser qualquer elemento, não apenas um botão
 *       </Button>
 *     </Tooltip>
 *   )
 * }
 * ```
 */
export const Tooltip = ({
  position = 'auto',
  children,
  description,
  action,
  title,
  content,
  interactive = false,
  delay = 0,
  ...props
}: TooltipProps) => {
  const popperInstance = useRef<Instance>()
  const tooltipReference = useRef<HTMLDivElement>()
  const childrenReference = useRef<HTMLDivElement>()
  const isInteractive = !!(interactive || action)
  const isInteractiveWithoutAction = interactive && !action

  const createInstance = useCallback(() => {
    if (!tooltipReference.current || !childrenReference.current) return

    popperInstance.current = createPopper(childrenReference.current, tooltipReference.current, {
      placement: position,
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, 20],
          },
        },
        {
          name: 'arrow',
          options: {
            padding: 5,
          },
        },
      ],
    })
  }, [position])

  const show = useCallback(() => {
    tooltipReference.current?.setAttribute('over-mouse', 'true')

    setTimeout(() => {
      if (!tooltipReference.current?.getAttribute('over-mouse')) return

      createInstance()

      tooltipReference.current?.setAttribute('data-show', '')

      popperInstance.current?.setOptions({
        modifiers: [{ name: 'eventListeners', enabled: true }],
      })
    }, delay)
  }, [createInstance, delay])

  const hide = useCallback(() => {
    tooltipReference.current?.removeAttribute('over-mouse')
    tooltipReference.current?.removeAttribute('data-show')
    popperInstance.current?.setOptions({
      modifiers: [{ name: 'eventListeners', enabled: false }],
    })
  }, [])

  return (
    <S.Container
      {...props}
      onMouseEnter={() => isInteractiveWithoutAction && show()}
      onMouseLeave={() => isInteractiveWithoutAction && hide()}
    >
      <div
        onMouseEnter={() => show()}
        onFocus={() => show()}
        onMouseLeave={() => !isInteractive && hide()}
        onBlur={() => !isInteractive && hide()}
        ref={(reference) => {
          if (reference) {
            childrenReference.current = reference
          }
        }}
      >
        {children}
      </div>

      <div
        role="tooltip"
        id="tooltip"
        ref={(reference) => {
          if (reference) {
            tooltipReference.current = reference
          }
        }}
      >
        {content || (
          <>
            {title && <S.TooltipTitle>{title}</S.TooltipTitle>}

            <S.TooltipDescription hasAction={!!action}>{description}</S.TooltipDescription>

            {action && <S.TooltipAction onClick={hide}>{action}</S.TooltipAction>}
          </>
        )}

        <S.TooltipArrow
          isInteractive={isInteractive}
          data-popper-arrow
          id="arrow"
          position={position}
        />
      </div>
    </S.Container>
  )
}
